import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowUpRight, HardHat, Check } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import PageSEO from "@/components/seo/PageSEO";
import { fetchServices } from "@/lib/api";

export default function ServicesPage() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchServices()
            .then(setServices)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-brand-cream" data-testid="services-page">
            <PageSEO
                title="Construction Services in Manchester — Residential, Commercial, Civil, Renovation | EB Construction"
                description="Full-service UK construction: residential builds, commercial fit-outs, industrial delivery, renovation and civil works. Manchester HQ, projects across the North West."
                canonicalPath="/services"
                jsonLd={services.map((s) => ({
                    "@context": "https://schema.org",
                    "@type": "Service",
                    name: s.title,
                    description: s.tagline,
                    serviceType: s.category || s.title,
                    provider: {
                        "@id": "https://elevatebuildconstruction.co.uk/#organization",
                    },
                    areaServed: [
                        { "@type": "AdministrativeArea", name: "Greater Manchester" },
                        { "@type": "AdministrativeArea", name: "North West England" },
                    ],
                }))}
            />
            <section className="relative overflow-hidden border-b border-brand-line py-24 md:py-32">
                <div className="container-x grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-6">
                        <Reveal>
                            <SectionLabel index={0} label="Services" />
                            <span className="rule-gold" aria-hidden="true" />
                            <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] md:text-7xl">
                                Capabilities at{" "}
                                <span className="accent-italic text-brand-gold">scale.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-8 font-body text-lg leading-relaxed text-brand-steel md:text-xl">
                                Four disciplines, one operating model. Whether you're delivering a bespoke home, a commercial campus, a heritage refit, or a piece of national infrastructure — the standard we apply is the same.
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15} className="md:col-span-6">
                        <div className="aspect-[4/5] w-full overflow-hidden border border-brand-line">
                            <img
                                src="/brand/services-detail.jpg"
                                alt="Architectural detail of a modern UK commercial construction site"
                                className="h-full w-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {loading && (
                <div className="container-x py-24 font-mono-tag text-brand-steel" data-testid="services-loading">
                    LOADING SERVICES…
                </div>
            )}

            {error && (
                <div className="container-x py-24" data-testid="services-error">
                    <p className="font-body text-brand-charcoal">
                        Could not load services. {error}
                    </p>
                </div>
            )}

            {!loading && !error && services.map((s, i) => {
                const Icon = LucideIcons[s.icon] || HardHat;
                const reverse = i % 2 === 1;
                return (
                    <section
                        key={s.id}
                        className="border-b border-brand-line py-20 md:py-28"
                        data-testid={`service-section-${s.slug}`}
                    >
                        <div className={`container-x grid items-center gap-12 md:grid-cols-12 ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
                            <Reveal className={reverse ? "md:col-span-6 md:col-start-7" : "md:col-span-6"}>
                                <div className="aspect-[4/5] overflow-hidden border border-brand-line">
                                    <img
                                        src={s.image}
                                        alt={s.title}
                                        className="h-full w-full object-cover transition-transform duration-[900ms] hover:scale-105"
                                    />
                                </div>
                            </Reveal>
                            <Reveal
                                delay={0.1}
                                className={reverse ? "md:col-span-5" : "md:col-span-6"}
                            >
                                <p className="font-mono-tag text-brand-gold">
                                    [ 0{i + 1} // {s.category || "Service"} ]
                                </p>
                                <div className="mt-4 flex items-center gap-4">
                                    <div className="grid h-14 w-14 place-items-center border border-brand-charcoal">
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <h2 className="font-display text-4xl font-semibold leading-[1.1] md:text-5xl">
                                        {s.title}
                                    </h2>
                                </div>
                                <p className="accent-italic mt-6 text-xl text-brand-charcoal">
                                    {s.tagline}
                                </p>
                                <p className="mt-4 font-body text-base leading-relaxed text-brand-steel">
                                    {s.description}
                                </p>

                                <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {s.capabilities.map((c) => (
                                        <li
                                            key={c}
                                            className="flex items-start gap-3 border-l-2 border-brand-gold bg-white p-4"
                                            data-testid={`capability-${s.slug}-${c.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 20)}`}
                                        >
                                            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                            <span className="font-body text-sm text-brand-charcoal">
                                                {c}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    to="/contact"
                                    className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal transition-colors hover:text-brand-gold"
                                    data-testid={`service-inquire-${s.slug}`}
                                >
                                    Enquire about {s.title}
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </Reveal>
                        </div>
                    </section>
                );
            })}

            <section className="bg-brand-charcoal py-20 text-brand-offwhite md:py-28">
                <div className="container-x flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                    <div>
                        <p className="font-mono-tag text-brand-gold">
                            [ NEXT STEP ]
                        </p>
                        <h3 className="mt-4 font-display text-3xl font-semibold leading-[1.1] md:text-4xl">
                            Ready to brief a project?
                        </h3>
                    </div>
                    <Link
                        to="/contact"
                        className="btn-primary"
                        data-testid="services-cta-quote"
                    >
                        Get a Quote
                        <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
